/**
 * DataUploadRouter - Top-level router for rebate data upload
 *
 * Provides mode selection between AI-powered transformation and manual CSV upload.
 * Routes to either ConvertDataPageWorkflow (AI) or ManualRebateCSVUpload (Manual).
 */

import React from 'react';
import { useNavigate, useLocation } from '@tanstack/react-router';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  SparkleIcon,
  UploadIcon,
  ArrowRightIcon,
  EnvelopeSimple,
} from '@phosphor-icons/react';
import paths from '@/paths';
import { usePendingUploads } from '@/api/spreadsheet/usePendingUploads';

const DataUploadRouter: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch pending uploads to show count
  const { data: pendingUploadsData } = usePendingUploads();
  const pendingCount =
    pendingUploadsData?.uploads?.filter((u) => u.status === 'pending').length ||
    0;

  // Check if we're on the landing page (no sub-route)
  const isLandingPage =
    location.pathname === paths.authenticated.rebates.uploadData;

  // If not on landing page, render will be handled by child routes
  if (!isLandingPage) {
    return null;
  }

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto py-12 px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold mb-3">Upload Rebate Data</h1>
          <p className="text-lg text-muted-foreground">
            Choose how you&apos;d like to upload your rebate invoice data
          </p>
        </div>

        {/* Mode Selection Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* AI-Powered Transform */}
          <Card
            className="cursor-pointer hover:border-primary hover:shadow-lg transition-all"
            onClick={() =>
              navigate({ to: paths.authenticated.rebates.uploadDataAI })
            }
          >
            <CardHeader className="space-y-4 pb-4">
              <div className="flex items-center justify-between">
                <SparkleIcon className="h-10 w-10 text-primary" />
                <ArrowRightIcon className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold mb-2">
                  AI-Powered Transform
                </h2>
                <p className="text-muted-foreground">
                  For messy spreadsheets or images
                </p>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Auto-detect columns and mappings</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Clean and normalize messy data</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Handle complex formats and hierarchies</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>OCR support for images and PDFs</span>
                </li>
              </ul>
              <div className="pt-4">
                <Button
                  className="w-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate({ to: paths.authenticated.rebates.uploadDataAI });
                  }}
                >
                  Start AI Workflow
                  <ArrowRightIcon className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Manual Upload */}
          <Card
            className="cursor-pointer hover:border-primary hover:shadow-lg transition-all"
            onClick={() =>
              navigate({ to: paths.authenticated.rebates.uploadDataManual })
            }
          >
            <CardHeader className="space-y-4 pb-4">
              <div className="flex items-center justify-between">
                <UploadIcon className="h-10 w-10 text-primary" />
                <ArrowRightIcon className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold mb-2">Manual Upload</h2>
                <p className="text-muted-foreground">
                  For pre-formatted CSV files
                </p>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Download CSV template with examples</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Upload ready-to-submit CSV file</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Fast validation and error checking</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Direct submission (no AI processing)</span>
                </li>
              </ul>
              <div className="pt-4">
                <Button
                  className="w-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate({
                      to: paths.authenticated.rebates.uploadDataManual,
                    });
                  }}
                >
                  Start Manual Upload
                  <ArrowRightIcon className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Email Uploads (Demo) */}
          <Card
            className="cursor-pointer hover:border-primary hover:shadow-lg transition-all"
            onClick={() =>
              navigate({ to: paths.authenticated.rebates.uploadDataEmailQueue })
            }
          >
            <CardHeader className="space-y-4 pb-4">
              <div className="flex items-center justify-between">
                <EnvelopeSimple className="h-10 w-10 text-primary" />
                <div className="flex items-center gap-2">
                  {pendingCount > 0 && (
                    <Badge variant="secondary" className="bg-primary/10">
                      {pendingCount} pending
                    </Badge>
                  )}
                  <ArrowRightIcon className="h-5 w-5 text-muted-foreground" />
                </div>
              </div>
              <div>
                <h2 className="text-2xl font-semibold mb-2">Email Uploads</h2>
                <p className="text-muted-foreground">
                  Process files sent via email
                </p>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start">
                  <span className="mr-2">-</span>
                  <span>Receive spreadsheets via email</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">-</span>
                  <span>View pending uploads queue</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">-</span>
                  <span>Start AI conversion from queue</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">-</span>
                  <span>Track processing status</span>
                </li>
              </ul>
              <div className="pt-4">
                <Button
                  className="w-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate({
                      to: paths.authenticated.rebates.uploadDataEmailQueue,
                    });
                  }}
                >
                  View Email Queue
                  <ArrowRightIcon className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Help Text */}
        <div className="mt-12 text-center">
          <p className="text-sm text-muted-foreground">
            Not sure which to choose?{' '}
            <span className="font-medium text-foreground">
              Use AI-Powered Transform
            </span>{' '}
            for most cases. It handles messy data automatically.
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            Use Manual Upload if you already have a properly formatted CSV file.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DataUploadRouter;
